import * as classnames from 'classnames';
import { FloatingLabelInput } from './FloatingLabelInput';
import * as React from 'react';
import styled from 'styled-components';
import { InputWrapper } from './InputWrapper';
import { RevealPassword } from './RevealPassword';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { AlertIcon, DoneIcon } from '@estee/elc-icons';
import {
    disabledInputTheme,
    inputTheme,
    AlertIconTheme,
    ValidIconTheme
} from '../theme/default-theme';
import { isAutofillElement } from '../utils/InputFieldUtils';

export const StyledInput = styled.input`
    text-overflow: ellipsis;
    white-space: nowrap;
    border-width: 1px;
    border-style: solid;
    line-height: 24px;
    border-radius: 4px;
    width: 100%;
    padding: 8px 0 8px 12px;
    box-sizing: border-box;
    font-size: 14px;
    ${(props: { readOnly: boolean | undefined }) =>
        props.readOnly ? disabledInputTheme : inputTheme};
`;

export interface IInputFieldProps {
    inputLabel: string;
    name: string;
    id?: string;
    customClassName?: string;
    customLabelClassName?: string;
    inputValue?: string;
    hasError?: boolean;
    isRequired?: boolean;
    hasAlert?: boolean;
    hasValidCheck?: boolean;
    readOnly?: boolean;
    ariaLive?: boolean;
    ariaLabel?: string;
    ariaDescribedBy?: string;
    inputType?: string;
    dataTestId?: string;
    maxLength?: number;
    disabled?: boolean;
    showPasswordLabel?: string;
    showPasswordDataTestId?: string;
    isAutoCompleteDisabled?: boolean;
    placeholder?: string;
    list?: string;
    autoComplete?: string;
    inputMode?: string;
    onChange?(ev: React.FormEvent<HTMLInputElement>): void;
    onKeyUp?(event: React.KeyboardEvent<EventTarget>): void;
    onBlur?(name: string): void;
    onFocus?(name: string): void;
    autoFocus?: boolean;
    onInputFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onInputBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onInput?: React.FormEventHandler<HTMLInputElement> | undefined;
}

const IconWrapper = styled.div`
    position: relative;
    float: right;
    top: -40px;
    right: 12px;
    height: 0;
`;

const StyledAlertIcon = styled(AlertIcon)`
    width: 24px;
    height: 24px;
    ${AlertIconTheme};
`;

const StyledValidIcon = styled(DoneIcon)`
    width: 24px;
    height: 24px;
    ${ValidIconTheme};
`;

@observer
export class InputField extends React.Component<IInputFieldProps> {
    public static defaultProps = {
        maxLength: 300,
        autoFocus: false
    };

    @observable private isPasswordVisible: boolean = false;

    @observable private forceMoveUp = false;

    protected ref: React.RefObject<HTMLInputElement> = React.createRef();

    private intervalId: ReturnType<typeof setInterval>;

    @action
    private onClick = () => {
        this.isPasswordVisible = !this.isPasswordVisible;
    };

    public render() {
        const props = this.props;
        const className = classnames('elc-input-field', 'js-input-field', props.customClassName);
        const ariaLive = props.ariaLive ? 'assertive' : undefined;
        const ariaAtomic = props.ariaLive ? true : undefined;
        const inputType = props.inputType ? props.inputType : 'text';
        const inputMode = props.inputMode as React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
        const autoComplete = props.autoComplete;
        const { ref, forceMoveUp, onBlur, onFocus } = this;

        return (
            <InputWrapper>
                <FloatingLabelInput
                    inputValue={props.inputValue}
                    inputLabel={props.inputLabel}
                    inputType={props.inputType}
                    onBlurHandler={onBlur}
                    onFocusHandler={onFocus}
                    markAsError={props.hasError}
                    customLabelClassName={props.customLabelClassName}
                    isRequired={props.isRequired}
                    moveUp={forceMoveUp || !!props.inputValue}
                >
                    <StyledInput
                        autoComplete={props.isAutoCompleteDisabled ? 'new-password' : autoComplete}
                        id={props.id}
                        aria-label={props.ariaLabel}
                        aria-invalid={props.hasError}
                        name={props.name}
                        type={this.isPasswordVisible ? 'text' : inputType}
                        value={props.inputValue}
                        onChange={props.onChange}
                        className={className}
                        aria-required={props.isRequired}
                        readOnly={props.readOnly}
                        data-test-id={props.dataTestId}
                        onKeyUp={props.onKeyUp}
                        maxLength={props.maxLength}
                        aria-live={ariaLive}
                        aria-atomic={ariaAtomic}
                        disabled={Boolean(props.disabled)}
                        placeholder={props.placeholder}
                        list={props.list}
                        aria-describedby={props.ariaDescribedBy}
                        inputMode={inputMode}
                        autoFocus={props.autoFocus}
                        onBlur={props.onInputBlur}
                        onFocus={props.onInputFocus}
                        onInput={props.onInput}
                        ref={ref}
                    />
                </FloatingLabelInput>
                {inputType === 'password' && (
                    <RevealPassword
                        showPasswordLabel={props.showPasswordLabel}
                        showPasswordDataTestId={props.showPasswordDataTestId}
                        isCrossed={!this.isPasswordVisible}
                        onClick={this.onClick}
                    />
                )}
                {props.hasAlert && (
                    <IconWrapper>
                        <StyledAlertIcon />
                    </IconWrapper>
                )}
                {!props.hasAlert && props.hasValidCheck && (
                    <IconWrapper>
                        <StyledValidIcon />
                    </IconWrapper>
                )}
            </InputWrapper>
        );
    }

    @action
    componentDidMount() {
        if (!this.props.autoComplete || this.props.inputValue) {
            return;
        }

        let currentTry = 0;
        const interval = 300;
        const maxTries = 10;

        const checkAutocomplete = () => {
            const {
                ref: { current }
            } = this;

            return current ? isAutofillElement(current) : false;
        };

        if (checkAutocomplete()) {
            this.forceMoveUp = true;
        } else {
            this.intervalId = setInterval(() => {
                currentTry += 1;

                if (currentTry > maxTries) {
                    clearInterval(this.intervalId);
                } else if (checkAutocomplete()) {
                    action(() => {
                        this.forceMoveUp = true;
                    })();
                    clearInterval(this.intervalId);
                }
            }, interval);
        }
    }

    @action
    componentWillUnmount() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    @action
    private onFocus = (name: string) => {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }

        this.props.onFocus?.(name);

        this.forceMoveUp = false;
    };

    @action
    private onBlur = (name: string) => {
        this.props.onBlur?.(name);

        this.forceMoveUp = false;
    };
}
